import Button from '@/components/homepage/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import RichText from '@/components/RichText/RichText';
import { subHeadersStyles } from '@/consts/builder';
import useMediaQuery from '@/hooks/useMediaQuery';
import { ActionsType } from '@/store/types/IBuilder';
import beTwMerge from '@/utils/beTwMerge';
import { getAnchorId, getComponentOutlineMargin, isRichTextEmpty } from '@/utils/builderUtils';
import Image from 'next/image';

type MultiImageProps = {
  anchorLinkID: string;
  hasGutters: boolean;
  images: {
    actions: ActionsType[];
    alt: string;
    copyColor: string;
    href?: string;
    position: string;
    src: string;
    subHeader?: string;
    title?: string;
  }[];
  margin: boolean;
  outlineMargin: boolean;
};
export default function MultiImage({ anchorLinkID, hasGutters, images, margin, outlineMargin }: MultiImageProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const guttersClass = hasGutters ? 'tw-gap-2' : '';

  const renderImageText = (index: number) => {
    const { actions, copyColor, subHeader, position, title } = images[index];
    const isTextWhite = copyColor === 'white';
    const slicedActions: ActionsType[] =
      actions?.slice(0, 1).map(action => ({
        ...action,
        color: (action.color as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
        mobileColor: (action.mobileColor as 'light' | 'dark') === 'dark' ? 'dark-green' : 'white',
      })) || [];

    return (
      <div
        className={beTwMerge(
          'tw-w-full tw-h-full tw-absolute tw-bottom-0 tw-pointer-events-none tw-flex tw-flex-col tw-items-center tw-gap-[16px] tw-px-[58px] tw-justify-end tw-py-4 large:tw-py-12',
          position === 'center' && 'tw-justify-center',
          position === 'top' && 'tw-justify-start'
        )}
      >
        <div className="tw-w-full tw-text-center">
          {title && (
            <h3 className={`tw-mb-0 tw-mt-0  ${subHeadersStyles['h3']} ${isTextWhite ? 'tw-text-color-white' : ''}`}>
              {title}
            </h3>
          )}
          {subHeader && !isRichTextEmpty(subHeader) && (
            <RichText
              className={`tw-mt-4 [&>p]:!tw-text-[14px] [&>p]:!tw-font-avenirmedium tw-pointer-events-auto ${isTextWhite ? 'tw-text-color-white' : ''}`}
              content={subHeader}
              isOnlyPcTextWhite={false}
              isTextWhite={isTextWhite}
            />
          )}
        </div>
        {slicedActions.length > 0 && (
          <div className="tw-pointer-events-auto tw-flex tw-w-full tw-justify-center">
            {slicedActions.map((action, i) => {
              const { color, isDropdownButton, label, mobileColor, mobileVariant, variant } = action;
              if (isDropdownButton && action.actions?.length) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    className="tw-min-w-[146px] tw-font-avenirmedium tw-capitalize tw-w-full [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis tw-font-avenirmedium"
                    color={isDesktop ? color : mobileColor}
                    key={i}
                    label={label}
                    options={subActions}
                    type={isDesktop ? variant : mobileVariant}
                  />
                );
              }
              return (
                <Button
                  className="tw-min-w-[146px] tw-font-avenirmedium tw-capitalize tw-w-full tw-whitespace-nowrap tw-text-ellipsis tw-overflow-hidden tw-font-avenirmedium"
                  color={isDesktop ? color : mobileColor}
                  href={action.href}
                  key={i}
                  label={label}
                  type={isDesktop ? variant : mobileVariant}
                />
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderImage = (index: number, width: string, isFullHeight: boolean = true) => {
    const { alt, href, src } = images[index];

    const image = (
      <Image
        alt={alt || ''}
        fill
        src={src}
        className="!tw-relative"
        style={{ aspectRatio: isFullHeight ? '1 / 1' : '2 / 1' }}
      />
    );
    return (
      <div className="tw-w-full tw-flex tw-flex-col tw-items-center tw-gap-[8px]" style={{ width }}>
        <div className={`tw-relative tw-w-full ${images.length > 2 && index === 0 ? 'tw-grow' : ''}`}>
          {href ? (
            <a href={href} className="tw-block tw-w-full tw-h-full">
              {image}
            </a>
          ) : (
            image
          )}
          {renderImageText(index)}
        </div>
      </div>
    );
  };

  const renderImageGrid = () => {
    switch (images.length) {
      case 2:
        return (
          <>
            {renderImage(0, isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%')}
            {renderImage(1, isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%')}
          </>
        );

      case 3:
        return (
          <>
            {renderImage(0, isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%')}
            <div
              className={`tw-relative tw-aspect-square tw-flex tw-flex-col ${guttersClass}`}
              style={{ width: isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%' }}
            >
              {renderImage(1, '100%', !isDesktop)}
              {renderImage(2, '100%', !isDesktop)}
            </div>
          </>
        );

      case 4:
        return (
          <>
            {renderImage(0, isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%')}
            <div
              className={`tw-relative tw-aspect-square tw-flex tw-flex-col ${guttersClass}`}
              style={{ width: isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%' }}
            >
              <div className={`tw-w-full tw-flex tw-flex-row ${guttersClass}`}>
                {renderImage(1, isDesktop && hasGutters ? 'calc(50% - 2px)' : '50%')}
                {renderImage(2, isDesktop && hasGutters ? 'calc(50% - 2px)' : '50%')}
              </div>
              {renderImage(3, '100%', false)}
            </div>
          </>
        );
      case 5:
        return (
          <>
            {renderImage(0, isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%')}
            <div
              className={`tw-relative tw-aspect-square tw-flex tw-flex-col ${guttersClass}`}
              style={{ width: isDesktop && hasGutters ? 'calc(50% - 4px)' : '100%' }}
            >
              <div className={`tw-w-full tw-flex tw-flex-row ${guttersClass}`}>
                {renderImage(1, isDesktop && hasGutters ? 'calc(50% - 2px)' : '50%')}
                {renderImage(2, isDesktop && hasGutters ? 'calc(50% - 2px)' : '50%')}
              </div>
              <div className={`tw-w-full tw-flex tw-flex-row ${guttersClass}`}>
                {renderImage(3, isDesktop && hasGutters ? 'calc(50% - 2px)' : '50%')}
                {renderImage(4, isDesktop && hasGutters ? 'calc(50% - 2px)' : '50%')}
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };
  return (
    <div
      className={beTwMerge(margin ? 'tw-mb-8 lg:tw-mb-10' : '', getComponentOutlineMargin(outlineMargin))}
      {...getAnchorId(anchorLinkID)}
    >
      {images && (
        <div>
          <div className={`tw-w-full tw-flex tw-h-auto tw-flex-col large:tw-flex-row ${guttersClass}`}>
            {renderImageGrid()}
          </div>
        </div>
      )}
    </div>
  );
}
